import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * useInactivityTimer
 *
 * Timer di inattività centralizzato per le chat (Pro, Operatori, Pubblica).
 * Allo scadere di `TIMEOUT_MS` mostra un toast informativo e mette la chat in
 * stato di "reset offerto": il consumer riceve `pendingReset=true` e può
 * mostrare due CTA all'utente:
 *   - "Continua questa chat"   → chiama `dismiss()`  (annulla il reset)
 *   - "Nuova chat"             → chiama `confirmReset()` (esegue `onReset`)
 *
 * ### Regole
 * - Il timer parte SOLO quando `enabled` è true (chat aperta) E ci sono
 *   messaggi (`hasMessages`).
 * - Il timer si mette in pausa mentre l'IA sta rispondendo (`isBusy=true`),
 *   così non si "resetta a metà frase".
 * - Ogni chiamata a `bump()` (nuovo messaggio, input dell'utente, ecc.)
 *   ripristina il timer.
 * - Quando `enabled` diventa false (chat chiusa) il timer viene fermato e
 *   qualsiasi `pendingReset` in corso viene annullato.
 * - Il valore di 20 minuti è definito **solo qui** come costante centrale.
 *
 * ### Uso
 * ```jsx
 * const { pendingReset, bump, dismiss, confirmReset } = useInactivityTimer({
 *   enabled: open,
 *   hasMessages: messages.length > 0,
 *   isBusy: sending,
 *   onReset: () => { setMessages([]); setSessionId(null); },
 * });
 * ```
 */
export const INACTIVITY_TIMEOUT_MS = 20 * 60 * 1000; // 20 minuti

export default function useInactivityTimer({
  enabled,
  hasMessages,
  isBusy = false,
  onReset,
  timeoutMs = INACTIVITY_TIMEOUT_MS,
  toastMessage = "La sessione chat è stata reimpostata per inattività",
}) {
  const [pendingReset, setPendingReset] = useState(false);
  const timerRef = useRef(null);
  const onResetRef = useRef(onReset);
  // Mantiene il riferimento al callback aggiornato senza forzare restart del timer
  onResetRef.current = onReset;

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const start = () => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      // Segnala al consumer: mostra le due CTA "continua/nuova chat".
      // Il vero reset (svuotamento messaggi / rigenerazione session) avviene
      // solo su `confirmReset()`. Il toast informa comunque l'utente.
      toast.info(toastMessage, { duration: 8000 });
      setPendingReset(true);
    }, timeoutMs);
  };

  // Main effect: gestisce ciclo vita del timer in base agli input.
  useEffect(() => {
    if (!enabled || !hasMessages || isBusy) {
      // Condizioni di NON avvio (o di pausa): stoppiamo qualsiasi timer attivo.
      clearTimer();
      return () => clearTimer();
    }
    start();
    return () => clearTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, hasMessages, isBusy, timeoutMs]);

  // Se la chat viene chiusa mentre c'è un pendingReset, lo annulliamo
  // (all'apertura successiva il timer riparte da zero se ci sono messaggi).
  useEffect(() => {
    if (!enabled && pendingReset) setPendingReset(false);
  }, [enabled, pendingReset]);

  /** Ripristina il timer (evento di attività dell'utente). */
  const bump = () => {
    if (!enabled || !hasMessages || isBusy) return;
    start();
  };

  /** Continua la chat corrente: annulla il pendingReset senza svuotare. */
  const dismiss = () => {
    setPendingReset(false);
    if (enabled && hasMessages && !isBusy) start();
  };

  /** Nuova chat: esegue `onReset` e chiude il pendingReset. */
  const confirmReset = () => {
    try {
      onResetRef.current && onResetRef.current();
    } finally {
      setPendingReset(false);
      clearTimer();
    }
  };

  return { pendingReset, bump, dismiss, confirmReset };
}
